/**
 * Date preset helpers for the offers page. Same presets as dashboard
 * but offers uses NY timezone for Roots date calculations.
 * Display labels always use calendar dates (parity with Ads Manager).
 */

export { formatCalendarDate, labelFromIsoRange } from "@/lib/dates"
import { formatCalendarDate, labelFromIsoRange } from "@/lib/dates"

export type OffersDatePreset =
  | "today"
  | "yesterday"
  | "thisweek"
  /** Custom-range picker quick preset only (not a toolbar button). */
  | "last7"
  | "last30"
  | "thismonth"
  | "custom"

export type OffersPresetTimezoneMode = "local" | "ny"

/** Get the current date in America/New_York timezone. */
function getNYDate(now: Date = new Date()): Date {
  const nyStr = now.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
  // Format: MM/DD/YYYY
  const [m, d, y] = nyStr.split("/").map(Number)
  return new Date(y, m - 1, d)
}

export interface PresetRange {
  startDate: string
  endDate: string
  displayLabel: string
}

export function computePresetRange(
  preset: OffersDatePreset,
  now: Date = new Date(),
  timezoneMode: OffersPresetTimezoneMode = "local",
): PresetRange {
  const today =
    timezoneMode === "ny"
      ? getNYDate(now)
      : new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let start = new Date(today)
  let end = new Date(today)

  if (preset === "yesterday") {
    start.setDate(start.getDate() - 1)
    end = new Date(start)
  } else if (preset === "last7") {
    // Last 7 complete days ending yesterday (today excluded).
    end.setDate(end.getDate() - 1)
    start = new Date(end)
    start.setDate(start.getDate() - 6)
  } else if (preset === "thisweek") {
    // Monday of the current week through today.
    const day = today.getDay() // 0=Sun … 6=Sat
    const daysFromMonday = day === 0 ? 6 : day - 1
    start = new Date(today)
    start.setDate(today.getDate() - daysFromMonday)
    end = new Date(today)
  } else if (preset === "last30") {
    // Previous calendar month
    const y = today.getFullYear()
    const m = today.getMonth()
    const lastMonthYear = m === 0 ? y - 1 : y
    const lastMonthNum = m === 0 ? 11 : m - 1
    start = new Date(lastMonthYear, lastMonthNum, 1)
    end = new Date(lastMonthYear, lastMonthNum + 1, 0)
  } else if (preset === "thismonth") {
    start = new Date(today.getFullYear(), today.getMonth(), 1)
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  }

  return {
    startDate: formatCalendarDate(start),
    endDate: formatCalendarDate(end),
    displayLabel: labelFromIsoRange(
      formatCalendarDate(start),
      formatCalendarDate(end),
    ),
  }
}
